import React from 'react';
import styled from 'styled-components';

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px 20px;
  text-align: center;
  background: #1a1a1a;
  border-radius: 8px;
  margin: 20px;
`;

const ErrorTitle = styled.h2`
  color: #ff6b6b;
  font-size: 1.5rem;
  margin-bottom: 16px;
`;

const ErrorMessage = styled.p`
  color: #ccc;
  font-size: 1rem;
  margin-bottom: 24px;
  max-width: 600px;
  line-height: 1.5;
`;

const ErrorButton = styled.button`
  background: #4CAF50;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #45a049;
  }
`;

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error for debugging
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Store error details in state
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // Try to perform memory cleanup
        try {
            if (typeof window !== 'undefined') {
                // Clear any large objects that might be causing issues
                if (window.cachedImages) {
                    window.cachedImages = {};
                }

                // Force garbage collection if available
                if (window.gc) {
                    window.gc();
                }
            }
        } catch (cleanupError) {
            console.warn('Error during cleanup:', cleanupError);
        }
    }

    handleReload = () => {
        // Clear any problematic localStorage data
        try {
            const cart = JSON.parse(localStorage.getItem('cart') || '[]');

            // Remove any items with large data URLs
            const cleanedCart = cart.map(item => {
                if (item.imageUrl && item.imageUrl.startsWith('data:image')) {
                    return {
                        ...item,
                        imageUrl: '/images/products/default-map.jpg',
                        _imageOptimized: true
                    };
                }
                return item;
            });

            localStorage.setItem('cart', JSON.stringify(cleanedCart));
        } catch (error) {
            console.warn('Error cleaning cart during reload:', error);
        }

        // Reload the page
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <ErrorContainer>
                    <ErrorTitle>Oops! Something went wrong</ErrorTitle>
                    <ErrorMessage>
                        The application encountered an unexpected error. This might be due to memory limitations
                        or large image data. We've attempted to clean up the issue automatically.
                    </ErrorMessage>
                    <ErrorButton onClick={this.handleReload}>
                        Reload Page
                    </ErrorButton>
                    {process.env.NODE_ENV === 'development' && this.state.error && (
                        <details style={{ marginTop: '20px', color: '#999', fontSize: '0.8rem' }}>
                            <summary>Error Details (Development)</summary>
                            <pre style={{ textAlign: 'left', overflow: 'auto', maxWidth: '100%' }}>
                                {this.state.error.toString()}
                                {this.state.errorInfo.componentStack}
                            </pre>
                        </details>
                    )}
                </ErrorContainer>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary; 